import React from 'react';

import { Box, Checkbox, HStack, Text, Tooltip } from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { customTheme } from 'theme';

import { PrioritizationFlowFilter } from 'api/scan-vault/prioritizationFlow';

import { EPSS_THRESHOLDS, FlowChainState, formatPercent } from './utils';

interface Props {
  chain: FlowChainState;
  onChange: (chain: FlowChainState) => void;
  /** Share removed by each applied filter for the selected measure. */
  deltas: Partial<Record<PrioritizationFlowFilter, number>>;
}

const selectStyle: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  padding: 0,
};

const FilterChip: React.FC<{
  filter: PrioritizationFlowFilter;
  chain: FlowChainState;
  onChange: Props['onChange'];
  delta?: number;
}> = ({ filter, chain, onChange, delta }) => {
  const enabled = chain.enabled[filter];
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: filter });
  const toggle = () =>
    onChange({ ...chain, enabled: { ...chain.enabled, [filter]: !enabled } });

  let label: React.ReactNode;
  switch (filter) {
    case PrioritizationFlowFilter.KnownExploited:
      label = 'Known exploited';
      break;
    case PrioritizationFlowFilter.Patchable:
      label = 'Patch available';
      break;
    case PrioritizationFlowFilter.Epss:
      label = (
        <HStack spacing={1}>
          <Text>EPSS ≥</Text>
          <select
            aria-label="EPSS threshold"
            value={chain.epssThreshold}
            style={selectStyle}
            onChange={e =>
              onChange({ ...chain, epssThreshold: Number(e.target.value) })
            }
          >
            {EPSS_THRESHOLDS.map(value => (
              <option key={value} value={value}>
                {Math.round(value * 100)}%
              </option>
            ))}
          </select>
        </HStack>
      );
      break;
    case PrioritizationFlowFilter.Severity:
      label = (
        <select
          aria-label="Minimum severity"
          value={chain.minSeverity}
          style={selectStyle}
          onChange={e =>
            onChange({
              ...chain,
              minSeverity: e.target.value === 'critical' ? 'critical' : 'high',
            })
          }
        >
          <option value="high">Critical + High</option>
          <option value="critical">Critical only</option>
        </select>
      );
      break;
  }

  return (
    <HStack
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
      }}
      spacing={2}
      h={8}
      pl={1}
      pr={3}
      borderRadius="full"
      border="1px solid"
      borderColor={enabled ? 'transparent' : customTheme.colors.gray['200']}
      bg={enabled ? customTheme.colors.selectedSideNavMenuBg : 'white'}
      color={enabled ? 'black' : customTheme.colors.gray['300']}
      fontSize="sm"
      boxShadow={isDragging ? 'md' : undefined}
      transitionProperty="background, border-color"
      transitionDuration="0.15s"
    >
      <Tooltip label="Drag to change the order" openDelay={400}>
        <Box
          as="button"
          type="button"
          ref={setActivatorNodeRef}
          aria-label={`Reorder ${filter.replace('_', ' ')} filter`}
          cursor="grab"
          px={1}
          color={customTheme.colors.gray['250']}
          lineHeight={1}
          fontSize="xs"
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </Box>
      </Tooltip>
      <Checkbox
        isChecked={enabled}
        onChange={toggle}
        size="sm"
        colorScheme="purple"
      >
        <Box as="span" fontSize="sm">
          {label}
        </Box>
      </Checkbox>
      {enabled && delta !== undefined && (
        <Tooltip label="Removed by this filter at its position in the chain">
          <Text
            fontSize="xs"
            fontFamily="mono"
            color={customTheme.colors.primary}
            bg="white"
            px={1.5}
            borderRadius="full"
          >
            −{formatPercent(delta)}
          </Text>
        </Tooltip>
      )}
    </HStack>
  );
};

export const FilterChain: React.FC<Props> = ({ chain, onChange, deltas }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  // Enabled filters lead so the chips read in the order they are applied.
  const display = [
    ...chain.order.filter(filter => chain.enabled[filter]),
    ...chain.order.filter(filter => !chain.enabled[filter]),
  ];
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = display.indexOf(active.id as PrioritizationFlowFilter);
    const to = display.indexOf(over.id as PrioritizationFlowFilter);
    onChange({ ...chain, order: arrayMove(display, from, to) });
  };

  return (
    <HStack spacing={2} flexWrap="wrap" rowGap={2}>
      <Text fontSize="sm" color={customTheme.colors.gray['300']}>
        Narrow by
      </Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={display}
          strategy={horizontalListSortingStrategy}
        >
          {display.map((filter, index) => (
            <React.Fragment key={filter}>
              {index > 0 && chain.enabled[filter] && (
                <Text
                  aria-hidden
                  fontSize="xs"
                  color={customTheme.colors.gray['250']}
                >
                  →
                </Text>
              )}
              <FilterChip
                filter={filter}
                chain={chain}
                onChange={onChange}
                delta={deltas[filter]}
              />
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
    </HStack>
  );
};
